import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Coffee, MapPin, Clock, Utensils, Heart, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const features = [
    {
      icon: <Utensils className="w-6 h-6" />,
      title: 'Worth every bite',
      description: 'From refreshing drinks to juicy burgers'
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Student Prices',
      description: 'Budget-friendly meals from ₹50'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Quick Service',
      description: 'Fast orders for busy schedules'
    }
  ];

  const menuHighlights = [
    { name: 'Blue Sea Surf', price: '', image: 'https://res.cloudinary.com/dql6llktr/image/upload/v1770326305/WhatsApp_Image_2026-02-06_at_02.40.36_lknddb.jpg' },
    { name: 'Chilli Chicken', price: '', image: 'https://res.cloudinary.com/dql6llktr/image/upload/v1770326305/WhatsApp_Image_2026-02-06_at_02.40.36_2_l79aph.jpg' },
    { name: 'Mexican Paneer Sandwitch', price: '', image: 'https://res.cloudinary.com/dql6llktr/image/upload/v1770326305/WhatsApp_Image_2026-02-06_at_02.40.36_1_ptd2s4.jpg' },
  ];

  return (
    <div className="page-wrapper" data-testid="home-page">
      {/* Hero Section - Full screen with hidden navbar */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image - Real A1 Cafe Photo - Shifted down */}
        <div 
          className="absolute inset-0 bg-cover"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/0fbya8iq_unnamed.webp)',
            backgroundPosition: 'center 30%'
          }}
        />
        {/* Dark overlay for text visibility */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Warm Glow Effect */}
        <div className="absolute inset-0 warm-glow pointer-events-none" />

        {/* Content - Shifted down */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <span 
              className="inline-block text-3xl md:text-4xl text-[#F2CC8F] mb-4 drop-shadow-lg"
              style={{ fontFamily: 'Caveat, cursive' }}
            >
              Welcome to
            </span>
            <h1 
              className="text-6xl md:text-8xl lg:text-9xl font-black text-white mb-6 tracking-tight drop-shadow-2xl"
              style={{ fontFamily: 'Fraunces, serif', textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}
            >
              A1 Cafe
            </h1>
            <p 
              className="text-2xl md:text-3xl text-white mb-4 max-w-2xl mx-auto font-semibold drop-shadow-lg"
              style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}
            >
              Your Go-To Café Near NITK
            </p>
            <p 
              className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto drop-shadow-md"
              style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}
            >
              Quality coffee, delicious snacks & student-friendly prices
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/menu" data-testid="hero-view-menu-btn">
              <Button className="btn-primary rounded-full px-8 py-6 text-lg font-bold">
                View Menu
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a 
              href="https://wa.me/919611475342?text=Hi! I'd like to place an order from A1 Cafe"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-order-btn"
            >
              <Button className="btn-whatsapp rounded-full px-8 py-6 text-lg font-bold">
                Order Now
              </Button>
            </a>
            <Link to="/contact" data-testid="hero-visit-btn">
              <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-bold border-white text-white hover:bg-white hover:text-[#4A3B32]">
                <MapPin className="w-5 h-5 mr-2" />
                Visit Us
              </Button>
            </Link>
          </motion.div>

          {/* Quick Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-16 flex flex-wrap items-center justify-center gap-8 text-white"
          >
            <div className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
              <Clock className="w-5 h-5 text-[#E07A5F]" />
              <span className="font-medium">11:45 AM - 10 PM</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
              <MapPin className="w-5 h-5 text-[#E07A5F]" />
              <span className="font-medium">NITK Hostel Area</span>
            </div>
            <div className="flex items-center gap-2 bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
              <Coffee className="w-5 h-5 text-[#E07A5F]" />
              <span className="!font-medium">Starting ₹50</span>
            </div>
          </motion.div>

          {/* Available on Swiggy & Zomato */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-8 flex items-center justify-center gap-4"
          >
            <span className="text-white/80 text-sm font-medium">Also available on:</span>
            <a 
              href="https://www.swiggy.com/menu/1242857?source=sharing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg px-3 py-2 flex items-center gap-2 hover:scale-105 transition-transform"
              data-testid="hero-swiggy-link"
            >
              <img src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/dfsl17ui_images.jfif" alt="Swiggy" className="h-6 w-6 object-contain" />
            </a>
            <a 
              href="https://zomato.onelink.me/xqzv/rcql1v1i" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-lg px-3 py-2 flex items-center gap-2 hover:scale-105 transition-transform"
              data-testid="hero-zomato-link"
            >
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Zomato_logo.png/120px-Zomato_logo.png" alt="Zomato" className="h-5" />
            </a>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-white rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#F9F7F2]" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span 
              variants={fadeInUp}
              className="font-accent text-2xl text-[#E07A5F] block mb-2"
            >
              Why Students Love Us
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-[#4A3B32]"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              More Than Just a Café
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-3xl p-8 text-center shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-[#F0EBE0] rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#E07A5F]">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#4A3B32] mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                  {feature.title}
                </h3>
                <p className="text-[#6D5D53]">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Menu Highlights */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#F0EBE0]" data-testid="menu-highlights-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="flex flex-col md:flex-row items-end justify-between mb-12"
          >
            <div>
              <motion.span 
                variants={fadeInUp}
                className="font-accent text-2xl text-[#E07A5F] block mb-2"
              >
                Popular Picks
              </motion.span>
              <motion.h2 
                variants={fadeInUp}
                className="text-4xl md:text-5xl font-bold text-[#4A3B32]"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Student Favorites
              </motion.h2>
            </div>
            <motion.div variants={fadeInUp}>
              <Link to="/menu" data-testid="view-full-menu-btn">
                <Button className="btn-secondary rounded-full px-6 py-3 mt-6 md:mt-0">
                  View Full Menu
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {menuHighlights.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="menu-card bg-white rounded-2xl overflow-hidden shadow-sm group"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-[#4A3B32]" style={{ fontFamily: 'Fraunces, serif' }}>
                    {item.name}
                  </h3>
                  <span className="text-xl font-bold text-[#E07A5F]">{item.price}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section with Location */}
      <section className="py-20 md:py-32 px-6 md:px-12 bg-[#4A3B32]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-white"
            >
              <span className="font-accent text-2xl text-[#F2CC8F] block mb-4">
                Come Visit Us!
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                We're Right Near Your Campus
              </h2>
              <p className="text-white/80 text-lg mb-8 leading-relaxed">
                Located in the heart of NITK's hostel area, A1 Cafe is just a short walk away. 
                Perfect for late-night cravings, study breaks, or hanging out with friends.
              </p>
              
              <div className="flex flex-col gap-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">NIT Karnataka, Hostel Area</p>
                    <p className="text-white/70">Near NITK Campus, Surathkal</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Open Every Day</p>
                    <p className="text-white/70">11:45 AM – 10:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Order Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a 
                  href="https://wa.me/919611475342?text=Hi! I'd like to place an order from A1 Cafe"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-whatsapp-btn"
                >
                  <Button className="btn-whatsapp rounded-full px-8 py-4 text-lg font-bold w-full sm:w-auto">
                    Order on WhatsApp
                  </Button>
                </a>
                <a href="tel:+919611475342" data-testid="cta-call-btn">
                  <Button variant="outline" className="rounded-full px-8 py-4 text-lg font-bold border-white text-white hover:bg-white hover:text-[#4A3B32] w-full sm:w-auto">
                    Call Now
                  </Button>
                </a>
              </div>

              {/* Swiggy & Zomato */}
              <div className="flex items-center gap-4">
                <span className="text-white/70 text-sm">Order online:</span>
                <a 
                  href="https://www.swiggy.com/menu/1242857?source=sharing" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg px-3 py-2 hover:scale-105 transition-transform"
                >
                  <img src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/dfsl17ui_images.jfif" alt="Swiggy" className="h-6 w-6 object-contain" />
                </a>
                <a 
                  href="https://zomato.onelink.me/xqzv/rcql1v1i" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg px-3 py-2 hover:scale-105 transition-transform"
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Zomato_logo.png/120px-Zomato_logo.png" alt="Zomato" className="h-5" />
                </a>
              </div>
            </motion.div>

            {/* Location Card with Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Styled location card */}
              <div className="bg-[#F9F7F2] rounded-3xl overflow-hidden shadow-xl">
                <div className="aspect-video relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/7gppsnng_unnamed%202%20%281%29.webp"
                    alt="A1 Cafe Location"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-[#E07A5F] text-white px-3 py-1 rounded-full text-sm font-medium">
                      📍 NITK Campus
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#4A3B32] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                    Find Us Easily
                  </h3>
                  <p className="text-[#6D5D53] mb-4">
                    Located near NITK hostels with beautiful garden seating area.
                  </p>
                  <a 
                    href="https://maps.app.goo.gl/jgFwpAgEZtdiEADL6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#4A3B32] text-white px-6 py-3 rounded-full font-medium hover:bg-[#3a2d26] transition-colors"
                    data-testid="google-maps-link"
                  >
                    <MapPin className="w-5 h-5" />
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
